import { Button, ButtonContent, type ButtonProps } from '@/ui/atoms/Button'
import { cn } from '@/utils/ui'
import Link from 'next/link'
import React from 'react'

import type { Page, Article } from '@/payload-types'
import { CMSButtonPropsType } from '../CMSButton'
import { useLocale } from 'next-intl'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'
import { localizedHref, normalizePath } from '@/utils/localizedHref'
import { isGatedFileUrl } from '@/lib/downloads/fileLinks'
import { GatedFileButton } from './GatedFileButton'
type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'articles'
    value: Page | Article | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
}

type CMSLinkProps = CMSButtonPropsType & {
  isSpecialLink?: boolean
}

export const CMSLink: React.FC<CMSLinkProps> = (props) => {
  const {
    link,
    className: classNameFromProps,
    label,
    appearance = 'inline',
    size: sizeFromProps,
    showIconLeft = true,
    iconLeft,
    showIconRight = true,
    iconRight: iconRightFromProps,
    isSpecialLink = false,
  } = props
  const { type, children, className, newTab, reference, url } = link as CMSLinkType

  const locale = useLocale()

  let iconRight = iconRightFromProps

  if (
    !iconRight &&
    label &&
    link?.url &&
    newTab &&
    !link?.url.startsWith('/') &&
    !link?.url.startsWith('mailto:') &&
    !link?.url.startsWith('tel:')
  ) {
    iconRight = 'external-link-line'
  }

  // Buttons that hand out one of our files go through the members-only download gate.
  if (type !== 'reference' && isGatedFileUrl(url) && appearance !== 'inline' && !isSpecialLink) {
    return (
      <GatedFileButton
        href={url as string}
        className={cn(className, classNameFromProps)}
        size={sizeFromProps}
        variant={appearance}
      >
        <ButtonContent
          showIconLeft={showIconLeft}
          iconLeft={iconLeft}
          label={label}
          showIconRight={showIconRight}
          iconRight={iconRightFromProps}
        >
          {children}
        </ButtonContent>
      </GatedFileButton>
    )
  }

  const href =
    type === 'reference' && typeof reference?.value === 'object' && reference.value.path
      ? `/${locale}${normalizePath(reference.value.path)}`
      : // Custom URLs: prefix the locale onto internal paths (idempotent +
        // external-safe), so e.g. a stored legacy CMS link (old /platform scheme) resolves
        // 200 instead of 301. Preserves null (renders nothing via the guard below).
        url
        ? localizedHref(url, locale)
        : url

  if (!href) return null

  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  const content = (
    <ButtonContent
      showIconLeft={showIconLeft}
      iconLeft={iconLeft}
      label={label}
      showIconRight={showIconRight}
      iconRight={iconRight}
    >
      {children}
    </ButtonContent>
  )

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline' || isSpecialLink) {
    return (
      <Link
        onClick={() => { sendGAEvent('event', 'linkClicked', {target: href || url || '', label: label}); trackEvent('linkClicked', { target: href || url || '', label }) }}
        className={
          isSpecialLink
            ? cn(
                '!text-body-S-semibold text-text-accent hover:text-blue-30 focus:text-text-accent underline',
                className,
                classNameFromProps,
              )
            : cn(className, classNameFromProps)
        }
        href={href || url || ''}
        {...newTabProps}
      >
        {content}
      </Link>
    )
  }

  return (
    <Button
      asChild
      className={cn(className, classNameFromProps)}
      size={sizeFromProps}
      variant={appearance}
    >
      <Link href={href || url || ''} {...newTabProps}>
        {content}
      </Link>
    </Button>
  )
}
